/**
 * O que todo tipo de voucher do esqueleto novo (#5024) diz fora do miolo: o card de status em
 * texto, o aviso de quem aprova, as linhas do grupo Solicitação, o total e a recuperação. O voo
 * (#5025) os montou primeiro; o hotel (#5028) e os outros tipos reusam. Mesma régua do resto do
 * builder: linha sem dado não nasce.
 *
 * As chaves de cópia seguem em `trips.flightVoucher.*` — nasceram no voo e dizem o mesmo em
 * todo tipo.
 */
import { voucherStatusName } from '@voll-intelligence/domain';
import type {
  VoucherRecoveryView,
  VoucherRequestRow,
  VoucherStatusView,
  VoucherValuesView,
} from './views/voucher_skeleton_view.js';
import type { DocumentContext } from './context.js';
import type { VoucherDocumentView } from './views/voucher_document_view.js';
import { dayMonth, dayTimeLabel, stripDateLabel } from './date_labels.js';
import { money } from './format.js';
import type { TripService, Voucher } from './services.js';
import {
  approvalsScreen,
  optionsRow,
  policyScreen,
  requesterScreen,
} from './voucher_request_screens.js';

export {
  dayMonth,
  dayRangeLabel,
  monthLabel,
  stripDateLabel,
  weekdayDateLabel,
  weekdayDayLabel,
  weekdayDayTimeLabel,
} from './date_labels.js';

/** "7,5 km" — vírgula no pt-BR: o tradutor interpola com `String(value)`. A corrida (#5029) e
 *  o reembolso de KM (#5035). */
export function distanceLabel(km: number | null | undefined, ctx: DocumentContext): string | null {
  if (km == null) return null;
  return ctx.t('trips.services.distanceValue', {
    value: new Intl.NumberFormat(ctx.locale, { maximumFractionDigits: 1 }).format(km),
  });
}

/** A data do pedido. O schema a exige, mas fixture e espelho antigo chegam sem ela — e o
 *  documento não quebra por uma data: a linha some. */
export function createdAtOf(voucher: Voucher): Date | null {
  return voucher.createdAt instanceof Date ? voucher.createdAt : null;
}

/** "Expira 2 out, 15:53" — o detalhe do card de status em aprovação. Sem a expiração na
 *  fonte, `null`. O filho de bagagem lê a do voucher dele (#5026). */
export function voucherExpiry(voucher: Voucher, ctx: DocumentContext): string | null {
  return voucher.expiresAt
    ? ctx.t('trips.flightVoucher.expiresOn', { date: dayTimeLabel(voucher.expiresAt, ctx.locale) })
    : null;
}

/** "Cancelado em 29 out 2026 · Despesa duplicada" — o detalhe do card do cancelado (#6787),
 *  com o que a fonte registrou: a data, o motivo, ou os dois. Sem nenhum, `null`: o card
 *  fica só com o nome, como antes. */
export function canceledDetail(voucher: Voucher, ctx: DocumentContext): string | null {
  const cancellation = voucher.cancellation;
  const parts = [
    cancellation?.at
      ? ctx.t('trips.flightVoucher.canceledOn', {
          date: stripDateLabel(cancellation.at, ctx.locale),
        })
      : null,
    cancellation?.reason?.trim() || null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** O código Voll do voucher, à parte do título (#7373): a tela o mostra inteiro e copiável no
 *  topo do miolo. Código vazio é ausência, como `null`. */
export function voucherCodeView(
  code: string | null | undefined,
  ctx: DocumentContext,
): { label: string; value: string; copy: string } | null {
  const value = code?.trim();
  return value
    ? { label: ctx.t('trips.voucher.code'), value, copy: ctx.t('trips.voucher.copyCode') }
    : null;
}

/** O rótulo com o código numa linha só ("Aéreo · VOLL-H1721690") — o que a tela separou, o
 *  texto corrido (compartilhar, PDF, título da aba) segue dizendo junto. */
export function withVoucherCode(label: string, code: { value: string } | null | undefined): string {
  return code ? `${label} · ${code.value}` : label;
}

/** O resumo do voucher para a área de transferência — as MESMAS linhas de
 *  identidade que o topo do documento mostra, uma por linha. Deriva da vista já
 *  montada de propósito: um segundo formatador divergiria do documento no primeiro
 *  ajuste, e aí o que se cola deixa de ser o que se vê. */
export function voucherShareText(view: VoucherDocumentView): string {
  return [
    withVoucherCode(view.eyebrow, view.code),
    view.heading,
    view.subheading,
    view.summary,
    view.total?.value,
  ]
    .filter(Boolean)
    .join('\n');
}

/** O card de status: o nome da VOLL antiga (o mesmo do selo da viagem) e a data do degrau.
 *  Em aprovação, a data é a da expiração do pedido ("Expira 2 out, 15:53"); sem ela na
 *  fonte, o card fica só com o nome. */
export function voucherStatusCard(
  service: TripService,
  ctx: DocumentContext,
): VoucherStatusView | null {
  const voucher = service.voucher;
  const on = (key: string, date: Date | null | undefined) =>
    date ? ctx.t(key, { date: stripDateLabel(date, ctx.locale) }) : null;
  const name = voucherStatusName({
    modal: voucher.modal,
    status: service.status,
    internalStatus: voucher.internalStatus,
    cancellationRequestedAt: voucher.cancellationRequestedAt,
    tags: voucher.tags,
  });
  if (!name) return null;
  const label = ctx.t(`trips.voucherStatus.${name}`);
  switch (service.status) {
    case 'CONFIRMED':
      return {
        label,
        detail:
          voucher.cancellationRequestedAt == null
            ? on('trips.flightVoucher.issuedOn', voucher.emittedAt)
            : on('trips.flightVoucher.requestedOn', voucher.cancellationRequestedAt),
      };
    case 'AWAITING_APPROVAL': {
      const expiry = voucherExpiry(voucher, ctx);
      return expiry ? { label, detail: expiry } : { label };
    }
    case 'IN_PROGRESS':
      return name === 'WAITING_FOR_EMISSION'
        ? { label }
        : { label, detail: on('trips.flightVoucher.requestedOn', createdAtOf(voucher)) };
    case 'CANCELLED': {
      const detail = canceledDetail(voucher, ctx);
      return detail ? { label, detail } : { label };
    }
    case 'DECLINED':
    case 'EXPIRED':
    case null:
    default:
      return { label };
  }
}

/** Antes da emissão — o `PRE_EMISSION` do portão de cancelamento (`gating.ts`): em aprovação ou
 *  solicitado (a cotação entra, como na faixa). Em emissão já não é: o BFF recusa. */
export function isPreEmission(service: TripService): boolean {
  if (service.status === 'AWAITING_APPROVAL') return true;
  return (
    service.status === 'IN_PROGRESS' && service.voucher.internalStatus !== 'WAITING_FOR_EMISSION'
  );
}

/** O código da reserva (localizador, confirmação) só da emissão em diante — antes dela o
 *  código ainda não é do fornecedor. O cancelado mantém o que havia. */
export function showsBookingCode(service: TripService): boolean {
  return service.status === 'CONFIRMED' || service.status === 'CANCELLED';
}

/** O que as linhas de Solicitação leem: o voucher e o status. Um serviço da viagem serve, e
 *  também o voucher FILHO de bagagem, que não vira serviço (#5026). */
type VoucherSubject = Pick<TripService, 'status' | 'voucher'>;

/** Quem precisa aprovar, na ordem da fonte. Fora de "em aprovação", ninguém. */
function pendingApproverNames(service: VoucherSubject, ctx: DocumentContext): string | null {
  if (service.status !== 'AWAITING_APPROVAL') return null;
  const names = (service.voucher.pendingApprovers ?? []).map((approver) => approver.name);
  if (names.length === 0) return null;
  return new Intl.ListFormat(ctx.locale, { type: 'conjunction' }).format(names);
}

/** O aviso em aprovação: "Aguardando aprovação de Ana Lima." (#4459 — o viajante sabe a quem
 *  cobrar) e, com a expiração na fonte, "Tarifa garantida até 2 out, 15:53; depois disso o
 *  valor pode mudar." Sem nenhum dos dois, o aviso não nasce. */
export function voucherNotice(service: TripService, ctx: DocumentContext): string | null {
  const names = pendingApproverNames(service, ctx);
  const expiresAt = service.status === 'AWAITING_APPROVAL' ? service.voucher.expiresAt : null;
  const parts = [
    names ? ctx.t('trips.flightVoucher.awaitingApprovalNotice', { names }) : null,
    expiresAt
      ? ctx.t('trips.flightVoucher.fareGuaranteed', {
          date: dayTimeLabel(expiresAt, ctx.locale),
        })
      : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

/** "Pendente · Ana Lima" enquanto falta alguém; senão "Aprovado por Ana Lima", da fonte.
 *  Sem nenhum dos dois, a linha não nasce. */
function approvalsValue(service: VoucherSubject, ctx: DocumentContext): string | null {
  const pending = pendingApproverNames(service, ctx);
  if (pending) return ctx.t('trips.flightVoucher.approvalsPending', { names: pending });
  const approved = (service.voucher.approvals ?? []).map((approval) => approval.name);
  if (approved.length === 0) return null;
  return ctx.t('trips.flightVoucher.approvedBy', {
    names: new Intl.ListFormat(ctx.locale, { type: 'conjunction' }).format(approved),
  });
}

/** "Cancelado por · Ana Lima · 29 out" — só no cancelado e só com quem cancelou na fonte
 *  (#6787); o cancelamento automático não nomeia ninguém e a linha não nasce. */
function canceledByRow(service: VoucherSubject, ctx: DocumentContext): VoucherRequestRow | null {
  const cancellation = service.status === 'CANCELLED' ? service.voucher.cancellation : null;
  if (!cancellation?.by) return null;
  return {
    key: 'canceledBy',
    label: ctx.t('trips.flightVoucher.canceledBy'),
    value: cancellation.by,
    ...(cancellation.at ? { valueTail: dayMonth(cancellation.at, ctx.locale) } : {}),
  };
}

/** "Dentro da política" / "1 exceção" (vermelho). Fora da política sem o motivo na fonte, o
 *  rótulo de sempre, também em vermelho. */
function policyRow(voucher: Voucher, ctx: DocumentContext): VoucherRequestRow | null {
  if (voucher.within == null) return null;
  const label = ctx.t('trips.flightVoucher.policy');
  if (voucher.within) {
    return { key: 'policy', label, value: ctx.t('trips.voucher.inPolicy') };
  }
  const breaches = voucher.policyBreaches?.length ?? 0;
  return {
    key: 'policy',
    label,
    value:
      breaches > 0
        ? ctx.t('trips.flightVoucher.policyExceptions', { count: breaches })
        : ctx.t('trips.voucher.outPolicy'),
    tone: 'destructive',
  };
}

/** A tela da linha só quando ela existe: linha sem tela fica sem a chave (só leitura). */
function withScreen(screen: VoucherRequestRow['screen']): Pick<VoucherRequestRow, 'screen'> {
  return screen ? { screen } : {};
}

/** O grupo Solicitação com o que o voucher do viajante carrega: quem pediu e quando, a
 *  política, as aprovações, as opções que a cotação apresentou e quem cancelou — cada linha
 *  com a tela que ela abre (#5249), quando há o que mostrar. */
export function voucherRequestRows(
  service: VoucherSubject,
  ctx: DocumentContext,
): VoucherRequestRow[] {
  const voucher = service.voucher;
  const createdAt = createdAtOf(voucher);
  // O nome corta com reticências; o dia do pedido fica sempre inteiro, no fim da linha.
  const requesterName = voucher.requester?.name?.trim() || null;
  const requestDay = createdAt ? dayMonth(createdAt, ctx.locale) : null;
  const requester = requesterName ?? requestDay;
  const requesterTail = requesterName ? requestDay : null;
  const approvals = approvalsValue(service, ctx);
  const policy = policyRow(voucher, ctx);
  const requesterLabel = ctx.t('trips.flightVoucher.requestedBy');
  const approvalsLabel = ctx.t('trips.flightVoucher.approvals');
  const pending =
    service.status === 'AWAITING_APPROVAL'
      ? (voucher.pendingApprovers ?? []).map((approver) => approver.name)
      : [];
  const rows: (VoucherRequestRow | null)[] = [
    requester
      ? {
          key: 'requester',
          label: requesterLabel,
          value: requester,
          ...(requesterTail ? { valueTail: requesterTail } : {}),
          ...withScreen(requesterScreen(voucher, createdAt, requesterLabel, ctx)),
        }
      : null,
    policy ? { ...policy, ...withScreen(policyScreen(voucher, policy.label, ctx)) } : null,
    approvals
      ? {
          key: 'approvals',
          label: approvalsLabel,
          value: approvals,
          ...withScreen(approvalsScreen(voucher, pending, approvalsLabel, ctx)),
        }
      : null,
    optionsRow(voucher, ctx),
    canceledByRow(service, ctx),
  ];
  return rows.filter((row) => row != null);
}

/** O total, com as parcelas que o tipo sabe nomear (o voo: tarifa e taxas). Sem parcela, só
 *  o total — parcela inventada num documento de conferência não fecha a conta. O rótulo é
 *  "Total" em todo estado (#5026). */
export function voucherValues(
  service: TripService,
  ctx: DocumentContext,
  rows: VoucherValuesView['rows'] = [],
): VoucherValuesView | null {
  const total = money(service.amount, service.currency);
  if (!total) return null;
  return {
    rows,
    total: {
      label: ctx.t('trips.flightVoucher.total'),
      value: total,
      struck: service.status === 'CANCELLED' || service.status === 'EXPIRED',
    },
  };
}

/** Recusado pede de novo, expirado cota de novo — as duas pela conversa. */
export function voucherRecovery(
  service: TripService,
  ctx: DocumentContext,
): VoucherRecoveryView | null {
  if (service.status === 'DECLINED') return { label: ctx.t('trips.flightVoucher.retry') };
  if (service.status === 'EXPIRED') return { label: ctx.t('trips.flightVoucher.requote') };
  return null;
}
